import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { NavigationInterface } from 'app/layout/components/navigation/models/navigation-interface';
import { InfoLaboratoryRoleProjection } from 'app/shared/prothesis-api-projections/info-laboratory-role.projection';
import { InfoUserLaboratoryProjection } from 'app/shared/prothesis-api-projections/info-user-laboratory.projection';
import isEqual from 'lodash-es/isEqual';
import { Observable, ReplaySubject } from 'rxjs';
import { AuthService } from '../auth.service';
import { ConfigurationService } from '../configuration.service';
import { DatadogContextStateService } from '../datadog-context-state.service';
import { AreaMenu } from '../left-menu/area-menu.enum';
import { LeftMenuService } from '../left-menu/left-menu.service';
import { NotifyService } from '../notify/notify.service';
import { SessionStorageService } from '../session-storage.service';
import { WorkingTenantServiceInterface } from '../working-tenant-service.interface';
import { TimeZoneSingleton } from './time-zone-singleton';

@Injectable({
  providedIn: 'root',
})
export class WorkingLaboratoryService implements WorkingTenantServiceInterface {
  public authService: AuthService;
  public exploredUuid$: Observable<string>;
  public tenantBasePath = '/Laboratory';

  private _configurationService = inject(ConfigurationService);
  private _contextStateService: DatadogContextStateService = inject(DatadogContextStateService);
  private _exploredUuidSubject = new ReplaySubject<string>();
  private _exploringFlag: boolean | null = false;
  private _fuseNavigationService = inject(FuseNavigationService);
  private _httpClient = inject(HttpClient);
  private _laboratoryCode: string | null;
  private _laboratoryDemoModeFlag: boolean | null;
  private _laboratoryDocoonOutBoundFlag: boolean | null;
  private _laboratoryLegalIdentifierSchemeCode: string | null;
  private _laboratoryName: string | null;
  private _laboratoryRateAutoPublishFlag: boolean | null;
  private _laboratoryRole: InfoLaboratoryRoleProjection | null;
  private _laboratoryTzDatabaseName: string | null;
  private _leftMenuService = inject(LeftMenuService);
  private _notifyService = inject(NotifyService);
  private _sessionStorageService = inject(SessionStorageService);
  private _tenantUuid: string | null;

  public constructor() {
    this._leftMenuService.workingLaboratoryService = this;
    this.exploredUuid$ = this._exploredUuidSubject.asObservable();
  }

  public get exploringFlag(): boolean | null {
    return this._exploringFlag ?? false;
  }

  public set exploringFlag(value: boolean | null) {
    const oldExploringFlag = this._exploringFlag;
    this._exploringFlag = value;

    if (this._exploringFlag !== oldExploringFlag) {
      if (this._exploringFlag !== undefined && this._exploringFlag !== null) {
        this._sessionStorageService.write('working_laboratory_exploringFlag', this._exploringFlag);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_exploringFlag');
      }
    }
  }

  public get laboratoryCode(): string | null {
    return this._laboratoryCode;
  }

  public set laboratoryCode(value: string | null) {
    const oldLaboratoryCode = this._laboratoryCode;
    this._laboratoryCode = value;

    if (this._laboratoryCode !== oldLaboratoryCode) {
      if (this._laboratoryCode) {
        this._sessionStorageService.write('working_laboratory_code', this._laboratoryCode);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_code');
      }
      this._contextStateService.setLaboratoryCode(this._laboratoryCode);
    }
  }

  public get laboratoryDemoModeFlag(): boolean | null {
    return this._laboratoryDemoModeFlag;
  }

  public set laboratoryDemoModeFlag(value: boolean | null) {
    const oldLaboratoryDemoModeFlag = this._laboratoryDemoModeFlag;
    this._laboratoryDemoModeFlag = value;

    if (this.laboratoryDemoModeFlag !== oldLaboratoryDemoModeFlag) {
      if (this._laboratoryDemoModeFlag !== null && this._laboratoryDemoModeFlag !== undefined) {
        this._sessionStorageService.write('working_laboratory_demoModeFlag', this._laboratoryDemoModeFlag);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_demoModeFlag');
      }
    }
  }

  public get laboratoryDocoonOutBoundFlag(): boolean | null {
    return this._laboratoryDocoonOutBoundFlag;
  }

  public set laboratoryDocoonOutBoundFlag(value: boolean | null) {
    const oldLaboratoryDocoonOutBoundFlag = this._laboratoryDocoonOutBoundFlag;
    this._laboratoryDocoonOutBoundFlag = value;

    if (this._laboratoryDocoonOutBoundFlag !== oldLaboratoryDocoonOutBoundFlag) {
      if (this._laboratoryDocoonOutBoundFlag !== null && this._laboratoryDocoonOutBoundFlag !== undefined) {
        this._sessionStorageService.write('working_laboratory_docoonOutBoundFlag', this._laboratoryDocoonOutBoundFlag);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_docoonOutBoundFlag');
      }
    }
  }

  public get laboratoryLegalIdentifierSchemeCode(): string | null {
    return this._laboratoryLegalIdentifierSchemeCode;
  }

  public set laboratoryLegalIdentifierSchemeCode(value: string | null) {
    this._laboratoryLegalIdentifierSchemeCode = value;
    if (value !== null && value !== undefined) {
      this._sessionStorageService.write('working_laboratory_legalIdentifierSchemeCode', this._laboratoryLegalIdentifierSchemeCode);
    } else {
      this._sessionStorageService.removeItem('working_laboratory_legalIdentifierSchemeCode');
    }
  }

  public get laboratoryName(): string | null {
    return this._laboratoryName;
  }

  public set laboratoryName(value: string | null) {
    const oldLaboratoryName = this._laboratoryName;
    this._laboratoryName = value;

    if (this._laboratoryName !== oldLaboratoryName) {
      if (this._laboratoryName) {
        this._sessionStorageService.write('working_laboratory_name', this._laboratoryName);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_name');
      }
    }
  }

  public get laboratoryRateAutoPublishFlag(): boolean | null {
    return this._laboratoryRateAutoPublishFlag;
  }

  public set laboratoryRateAutoPublishFlag(value: boolean | null) {
    const oldLaboratoryRateAutoPublishFlag = this._laboratoryRateAutoPublishFlag;
    this._laboratoryRateAutoPublishFlag = value;

    if (this._laboratoryRateAutoPublishFlag !== oldLaboratoryRateAutoPublishFlag) {
      if (this._laboratoryRateAutoPublishFlag !== undefined && this._laboratoryRateAutoPublishFlag !== null) {
        this._sessionStorageService.write('working_laboratory_rate_auto_publish_flag', this._laboratoryRateAutoPublishFlag);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_rate_auto_publish_flag');
      }
    }
  }

  public get laboratoryRole(): InfoLaboratoryRoleProjection | null {
    return this._laboratoryRole;
  }

  public set laboratoryRole(value: InfoLaboratoryRoleProjection | null) {
    const oldLaboratoryRole = this._laboratoryRole;
    this._laboratoryRole = value;

    if (!isEqual(this._laboratoryRole, oldLaboratoryRole)) {
      if (this._laboratoryRole) {
        this._sessionStorageService.write('working_laboratory_role', this._laboratoryRole);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_role');
      }
    }
  }

  public get laboratoryTzDatabaseName(): string | null {
    return this._laboratoryTzDatabaseName;
  }

  public set laboratoryTzDatabaseName(value: string | null) {
    const oldLaboratoryTzDatabaseName = this._laboratoryTzDatabaseName;
    this._laboratoryTzDatabaseName = value;

    if (this._laboratoryTzDatabaseName !== oldLaboratoryTzDatabaseName) {
      TimeZoneSingleton.setInstance(this._laboratoryTzDatabaseName);
      if (this._laboratoryTzDatabaseName) {
        this._sessionStorageService.write('working_laboratory_tzDatabaseName', this._laboratoryTzDatabaseName);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_tzDatabaseName');
      }
    }
  }

  public get tenantUuid(): string | null {
    return this._tenantUuid;
  }

  public set tenantUuid(value: string | null) {
    const oldTenantUuid = this._tenantUuid;
    this._tenantUuid = value;

    if (this._tenantUuid !== oldTenantUuid) {
      if (this._tenantUuid) {
        this._sessionStorageService.write('working_laboratory_uuid', this._tenantUuid);
      } else {
        this._sessionStorageService.removeItem('working_laboratory_uuid');
      }
    }
  }

  public clear(): void {
    this.exploringFlag = null;
    this.laboratoryCode = null;
    this.laboratoryDemoModeFlag = null;
    this.laboratoryDocoonOutBoundFlag = null;
    this.laboratoryLegalIdentifierSchemeCode = null;
    this.laboratoryName = null;
    this.laboratoryRateAutoPublishFlag = null;
    this.laboratoryRole = null;
    this.laboratoryTzDatabaseName = null;
    this.tenantUuid = null;
  }

  public emitExploredUuid(): void {
    this._exploredUuidSubject.next(this._tenantUuid!);
  }

  public explore(laboratoryUuid: string | null | undefined, notifyFlag = true): Observable<InfoUserLaboratoryProjection> {
    this.exploringFlag = true;

    return new Observable<InfoUserLaboratoryProjection>((observer) => {
      this.getInfo(laboratoryUuid).subscribe(
        (infoUserLaboratoryProjection: InfoUserLaboratoryProjection) => {
          // tenantUuid must be set before exploreInternal, which emits on _exploredUuidSubject
          // using _tenantUuid, which must already hold the correct value.
          this.tenantUuid = infoUserLaboratoryProjection.laboratory.uuid;
          this.laboratoryCode = infoUserLaboratoryProjection.laboratory.code;
          this.laboratoryDemoModeFlag = infoUserLaboratoryProjection.laboratory.demoModeFlag;
          this.laboratoryDocoonOutBoundFlag = infoUserLaboratoryProjection.laboratory.docoonOutBoundFlag;
          this.laboratoryLegalIdentifierSchemeCode = infoUserLaboratoryProjection.laboratory.legalIdentifierSchemeCode;
          this.laboratoryName = infoUserLaboratoryProjection.laboratory.name;
          this.laboratoryRateAutoPublishFlag = infoUserLaboratoryProjection.laboratory.rateAutoPublishFlag;
          this.laboratoryRole = infoUserLaboratoryProjection.laboratoryRole;
          this.laboratoryTzDatabaseName = infoUserLaboratoryProjection.laboratory.tzDatabaseName;
          this.exploreInternal(notifyFlag);
          observer.next(infoUserLaboratoryProjection);
          observer.complete();
        },
        () => {
          observer.complete();
        },
      );
    });
  }

  public getInfo(laboratoryUuid: string | null | undefined): Observable<InfoUserLaboratoryProjection> {
    const actionUrl = `${this._configurationService.ProthesisApiAddress}/Laboratory/${laboratoryUuid}/GetInfo`;
    return this._httpClient.get<InfoUserLaboratoryProjection>(actionUrl);
  }

  public stopExploring(): void {
    const laboratoryName = this._laboratoryName;
    this.clear();

    // Set navigation menu
    const navigation = this._leftMenuService.getNavigation([AreaMenu.Admin], null, true, laboratoryName);
    this._fuseNavigationService.unregister('main');
    this._fuseNavigationService.register('main', navigation);

    if (laboratoryName) {
      this._notifyService.displayInformationMessagesNow(`${laboratoryName} n'est plus en cours d'exploration.`);
    }

    this._exploredUuidSubject.next(null!);
  }

  public tryExploring(): void {
    this._exploringFlag = this._sessionStorageService.read('working_laboratory_exploringFlag');
    this.laboratoryCode = this._sessionStorageService.read('working_laboratory_code')!;
    this._laboratoryDemoModeFlag = this._sessionStorageService.read('working_laboratory_demoModeFlag');
    this._laboratoryDocoonOutBoundFlag = this._sessionStorageService.read('working_laboratory_docoonOutBoundFlag');
    this._laboratoryLegalIdentifierSchemeCode = this._sessionStorageService.read('working_laboratory_legalIdentifierSchemeCode');
    this._laboratoryName = this._sessionStorageService.read('working_laboratory_name');
    this._laboratoryRateAutoPublishFlag = this._sessionStorageService.read('working_laboratory_rate_auto_publish_flag');
    this._laboratoryRole = this._sessionStorageService.read('working_laboratory_role');
    this._laboratoryTzDatabaseName = this._sessionStorageService.read('working_laboratory_tzDatabaseName');
    TimeZoneSingleton.setInstance(this._laboratoryTzDatabaseName);
    this._tenantUuid = this._sessionStorageService.read('working_laboratory_uuid');

    if (
      this._laboratoryRole &&
      this._exploringFlag !== null &&
      this._exploringFlag !== undefined &&
      this._laboratoryCode &&
      this._laboratoryDemoModeFlag !== null &&
      this._laboratoryDemoModeFlag !== undefined &&
      this._laboratoryName &&
      this._laboratoryRateAutoPublishFlag !== null &&
      this._laboratoryRateAutoPublishFlag !== undefined &&
      this._laboratoryTzDatabaseName &&
      this._tenantUuid
    ) {
      this.exploreInternal(false);
    }
  }

  private exploreInternal(notifyFlag: boolean): void {
    // Set navigation menu
    const navigation = this._leftMenuService.getNavigation(
      [AreaMenu.Admin, AreaMenu.Laboratory],
      this.laboratoryDemoModeFlag,
      true,
      this._laboratoryName,
    );

    const navigationItem: NavigationInterface | undefined = navigation.find(
      (fuseNavigationItem: NavigationInterface) => fuseNavigationItem.id === 'laboratory-area',
    );
    navigationItem!.translate = undefined;
    navigationItem!.text = `${this.laboratoryCode} - ${this._laboratoryName}`;
    this._fuseNavigationService.unregister('main');
    this._fuseNavigationService.register('main', navigation);

    if (notifyFlag) {
      this._notifyService.displayInformationMessagesNow(`${this._laboratoryName} en cours d'exploration.`);
    }

    this._exploredUuidSubject.next(this.tenantUuid!);
  }
}
